import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, And, Rule, WorkflowStepInterface } from '@vendasta/automata';
import { PipelineInterface } from '@vendasta/sales-opportunities';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as CloseLostReasonFuncs from './close-lost-reason';
import { CloseLostReason } from './close-lost-reason';
import { LAST_EDIT_ACTION_CONDITION_INDEX, OPPORTUNITY_STATUS_INDEX, RulePath } from './consts';
import { LastEditActionCondition } from './last-edit-action-condition';
import * as OpportunityStatusFuncs from './opportunity-status';
import { OpportunityStatus } from './opportunity-status';

import { SalesOpportunitiesService } from '../../../../data-services/sales-opportunities.service';
import { AutomationErrorHandlerService } from '../../../services';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';

interface GetActionSubtitleSuccessStateParams {
  pipelineId: string;
  lastEditAction: string;
  opportunityStatus: string;
  rule: Rule;
}

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example {@link AccountGroupCreatedService}
 */
@Injectable({ providedIn: 'root' })
export class OpportunityUpdatedService implements CommonDisplayActionService, CommonDisplayActivityService {
  constructor(
    private readonly translateService: TranslateService,
    protected readonly salesOpportunitiesService: SalesOpportunitiesService,
    private readonly errorHandler: AutomationErrorHandlerService,
  ) {}

  private getPipelineStageName(pipeline: PipelineInterface, stageProbability: number): string {
    const stage = pipeline.stages.find((s) => {
      const probability = s.probability || 0;
      return probability === stageProbability;
    });
    return stage?.name || this.translateService.instant('AUTOMATIONS.COMMON.UNKNOWN');
  }

  private getPipelineName(pipeline: PipelineInterface, pipelineId: string): string {
    if (pipelineId === '*') {
      return this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.COMMON.ANY');
    }
    return pipeline?.name || this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.DEFAULT_PIPELINE');
  }

  private getActivityErrorMessage(error: any): string {
    console.error(error);
    return this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTIVITY.ERROR_LOADING_ACTIVITY');
  }

  getTemplateActionSubtitle(
    _namespace: string,
    step: WorkflowStepInterface,
    isWaitUntil?: boolean,
  ): Observable<string> {
    return this.getActionSubtitleEmptyState(step, isWaitUntil);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    const rule = And.fromRuleInterface(step.choices.rules[0]);

    const lastEditActionCondition = this.getLastEditActionConditionFromRule(rule);
    const opportunityStatus = this.getOpportunityStatusFromRule(rule);

    const pipelineId = rule.listValuesByVariablePath(RulePath.pipelineIdPath)[0] || null;

    if (!pipelineId || !opportunityStatus) {
      if (!opportunityStatus && !pipelineId) {
        this.errorHandler.setValidationError({
          stepId: step?.id,
          taskDefinitionId: step?.taskDefinitionId,
          errorMessage: this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.CONFIG_ERROR'),
          isNonDismissable: true,
        });
      } else {
        return this.getActionSubtitleEmptyState(step, isWaitUntil);
      }
    }

    if (!!pipelineId && !!opportunityStatus) {
      return this.getActionSubtitleSuccessState(
        { pipelineId, lastEditAction: lastEditActionCondition, opportunityStatus, rule },
        isWaitUntil,
      ).pipe(
        catchError(() => {
          return this.getActionSubtitleErrorState({}, step);
        }),
      );
    }
  }

  getLastEditActionConditionFromRule(rule: Rule): LastEditActionCondition {
    const lastEditActionConditionRule = rule.value[LAST_EDIT_ACTION_CONDITION_INDEX];
    let lastEditActionCondition: LastEditActionCondition;
    if (Array.isArray(lastEditActionConditionRule.value)) {
      lastEditActionCondition = LastEditActionCondition.CreatedOrUpdated;
    } else {
      lastEditActionCondition = rule.listValuesByVariablePath(RulePath.lastEditActionPath)[0];
    }
    return lastEditActionCondition;
  }

  getOpportunityStatusFromRule(rule: Rule): OpportunityStatus {
    const statusRule = rule.value[OPPORTUNITY_STATUS_INDEX];
    let opportunityStatus: OpportunityStatus;
    if (Array.isArray(statusRule.value)) {
      opportunityStatus = OpportunityStatus.ClosedAny;
    } else {
      opportunityStatus = rule.listValuesByVariablePath(RulePath.pipelineStatusPath)[0];
    }
    return opportunityStatus;
  }

  getActionSubtitleSuccessState(
    { pipelineId, lastEditAction, opportunityStatus, rule }: GetActionSubtitleSuccessStateParams,
    isWaitUntil?: boolean,
  ): Observable<string> {
    const pipeline$ = this.salesOpportunitiesService.pipelines$.pipe(
      switchMap((pipelines) => {
        if (pipelineId === CloseLostReason.AnyStar) {
          return of({ pipelineId: CloseLostReason.AnyStar } as PipelineInterface);
        }
        const pipeline = pipelines.find((p) => p.pipelineId === pipelineId);

        if (!pipeline) {
          return throwError(`Pipeline ${pipelineId} not found`);
        }

        return of(pipeline);
      }),
    );

    if (lastEditAction !== LastEditActionCondition.Closed) {
      const stageProbability = rule.listValuesByVariablePath(RulePath.probabilityPath)[0];
      return pipeline$.pipe(
        map((pipeline) => {
          const pipelineName = this.getPipelineName(pipeline, pipelineId);
          const stageName = this.getPipelineStageName(pipeline, stageProbability);

          let textKey: string;
          switch (lastEditAction) {
            case LastEditActionCondition.Create:
              if (isWaitUntil) {
                textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WAIT_UNTIL_CREATED_IN_PIPELINE_WITH_STAGE';
              } else {
                textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WHEN_CREATED_IN_PIPELINE_WITH_STAGE';
              }
              break;
            case LastEditActionCondition.UpdateProbability:
              if (isWaitUntil) {
                textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WAIT_UNTIL_UPDATED_IN_PIPELINE_WITH_STAGE';
              } else {
                textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WHEN_UPDATED_IN_PIPELINE_WITH_STAGE';
              }
              break;
            default:
              if (isWaitUntil) {
                textKey =
                  'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WAIT_UNTIL_CREATED_OR_CHANGED_IN_PIPELINE_WITH_STAGE';
              } else {
                textKey =
                  'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WHEN_CREATED_OR_CHANGED_IN_PIPELINE_WITH_STAGE';
              }
          }
          return this.translateService.instant(textKey, {
            pipelineName,
            stageName,
          });
        }),
      );
    }

    if (opportunityStatus === OpportunityStatus.ClosedLost) {
      const closedLostReason =
        rule.listValuesByVariablePath(RulePath.closedLostReasonPath)[0] || CloseLostReason.AnyStar;
      return pipeline$.pipe(
        map((pipeline) => {
          const pipelineName = this.getPipelineName(pipeline, pipelineId);
          let textKey =
            'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WHEN_CLOSED_ACTION_WITH_LOST_REASON_IN_PIPELINE';
          if (isWaitUntil) {
            textKey =
              'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WAIT_UNTIL_CLOSED_ACTION_WITH_LOST_REASON_IN_ANY_PIPELINE';
          }
          return this.translateService.instant(textKey, {
            pipelineName: pipelineName,
            closedLostReason: this.translateService.instant(CloseLostReasonFuncs.I18NKey(closedLostReason)),
          });
        }),
      );
    }

    return pipeline$.pipe(
      map((pipeline) => {
        const pipelineName = this.getPipelineName(pipeline, pipelineId);
        let textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WHEN_CLOSED_ACTION_IN_PIPELINE';
        if (isWaitUntil) {
          textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WAIT_UNTIL_CLOSED_ACTION_IN_PIPELINE';
        }
        return this.translateService.instant(textKey, {
          status: this.translateService.instant(OpportunityStatusFuncs.I18NKey(opportunityStatus)),
          pipelineName: pipelineName,
        });
      }),
    );
  }

  getActionSubtitleErrorState(data: any, step: WorkflowStepInterface): Observable<string> {
    this.errorHandler.setValidationError({
      stepId: step?.id,
      taskDefinitionId: step?.taskDefinitionId,
      errorMessage: this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.CONFIG_ERROR'),
      isNonDismissable: true,
    });

    return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.OPPORTUNITY_UPDATED');
  }

  getActionSubtitleEmptyState(step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    const rule = And.fromRuleInterface(step.choices.rules[0]);

    const lastEditActionCondition = this.getLastEditActionConditionFromRule(rule);
    const opportunityStatus = this.getOpportunityStatusFromRule(rule);

    if (opportunityStatus !== OpportunityStatus.Open) {
      if (opportunityStatus === OpportunityStatus.ClosedLost) {
        const closedLostReason =
          rule.listValuesByVariablePath(RulePath.closedLostReasonPath)[0] || CloseLostReason.AnyStar;

        let closedLostTextKey =
          'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WHEN_CLOSED_ACTION_WITH_LOST_REASON_IN_ANY_PIPELINE';
        if (isWaitUntil) {
          closedLostTextKey =
            'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WAIT_UNTIL_CLOSED_ACTION_WITH_LOST_REASON_IN_ANY_PIPELINE';
        }
        return this.translateService.stream(closedLostTextKey, {
          closedLostReason: this.translateService.instant(CloseLostReasonFuncs.I18NKey(closedLostReason)),
        });
      }
      let closedTextKey = 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WHEN_CLOSED_ACTION_IN_ANY_PIPELINE';
      if (isWaitUntil) {
        closedTextKey = 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WAIT_UNTIL_CLOSED_ACTION_IN_ANY_PIPELINE';
      }
      return this.translateService.stream(closedTextKey, {
        status: this.translateService.instant(OpportunityStatusFuncs.I18NKey(opportunityStatus)),
      });
    }

    if (lastEditActionCondition === LastEditActionCondition.CreatedOrUpdated) {
      let textKey =
        'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WHEN_OPPORTUNITY_CREATED_OR_UPDATED_ANY_STAGE_ANY_PIPELINE';
      if (isWaitUntil) {
        textKey =
          'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WAIT_UNTIL_OPPORTUNITY_CREATED_OR_UPDATED_ANY_STAGE_ANY_PIPELINE';
      }
      return this.translateService.stream(textKey);
    } else if (lastEditActionCondition === LastEditActionCondition.Create) {
      let textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WHEN_OPPORTUNITY_CREATED_ANY_STAGE_ANY_PIPELINE';
      if (isWaitUntil) {
        textKey =
          'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WAIT_UNTIL_OPPORTUNITY_CREATED_ANY_STAGE_ANY_PIPELINE';
      }
      return this.translateService.stream(textKey);
    } else if (lastEditActionCondition === LastEditActionCondition.UpdateProbability) {
      let textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WHEN_OPPORTUNITY_UPDATED_ANY_STAGE_ANY_PIPELINE';
      if (isWaitUntil) {
        textKey =
          'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTION.WAIT_UNTIL_OPPORTUNITY_UPDATED_ANY_STAGE_ANY_PIPELINE';
      }
      return this.translateService.stream(textKey);
    }

    return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.CONFIG_ERROR');
  }

  getActivityTitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.ENTERED_AUTOMATION');
  }

  getActivitySubtitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const triggerData = activity?.triggerData ? JSON.parse(activity.triggerData) : {};
    const lastEditActionCondition = triggerData.last_edit_action;
    const opportunityStatus = triggerData.pipeline_status;
    const closedLostReason = triggerData.closed_lost_reason;
    const pipelineId = triggerData.pipeline_id || '';
    const stageProbability = +triggerData.probability || 0;
    const opportunityId = triggerData.opportunity_id;
    const accountGroupId = triggerData.account_group_id;
    const opportunityName$ = this.salesOpportunitiesService.getOpportunityLink(opportunityId, accountGroupId);
    const pipeline$ = this.salesOpportunitiesService.pipelines$.pipe(
      map((pipelines) => {
        return pipelines.find((p) => p.pipelineId === pipelineId);
      }),
    );

    if (pipelineId === '') {
      return opportunityName$.pipe(
        map((opportunityName) => {
          switch (lastEditActionCondition) {
            case LastEditActionCondition.Create:
              return this.translateService.instant(
                'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTIVITY.EMPTY_ACTIVITY_CREATED',
                {
                  opportunityName: opportunityName,
                },
              );
            case LastEditActionCondition.UpdateProbability:
              return this.translateService.instant(
                'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTIVITY.EMPTY_ACTIVITY_UPDATED',
                {
                  opportunityName: opportunityName,
                },
              );
            case LastEditActionCondition.Closed:
              if (opportunityStatus === OpportunityStatus.ClosedLost) {
                return this.translateService.instant(
                  'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTIVITY.EMPTY_ACTIVITY_CLOSED_LOST_WITH_LOST_REASON',
                  {
                    opportunityName: opportunityName,
                    closedLostReason: this.translateService.instant(CloseLostReasonFuncs.I18NKey(closedLostReason)),
                  },
                );
              } else {
                return this.translateService.instant(
                  'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTIVITY.EMPTY_ACTIVITY_CLOSED_WON',
                  { opportunityName: opportunityName },
                );
              }
            default:
              return this.translateService.instant(
                'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTIVITY.ERROR_LOADING_ACTIVITY',
              );
          }
        }),
        catchError((err) => of(this.getActivityErrorMessage(err))),
      );
    }

    if (lastEditActionCondition === LastEditActionCondition.Create) {
      return combineLatest([pipeline$, opportunityName$]).pipe(
        map(([pipeline, opportunityName]) => {
          const pipelineName = this.getPipelineName(pipeline, pipelineId);
          const stageName = this.getPipelineStageName(pipeline, stageProbability);
          return this.translateService.instant(
            'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTIVITY.CREATED_PIPELINE_STAGE',
            {
              opportunityName,
              pipelineName,
              stageName,
            },
          );
        }),
        catchError((err) => of(this.getActivityErrorMessage(err))),
      );
    }

    if (lastEditActionCondition === LastEditActionCondition.UpdateProbability) {
      return combineLatest([pipeline$, opportunityName$]).pipe(
        map(([pipeline, opportunityName]) => {
          const pipelineName = this.getPipelineName(pipeline, pipelineId);
          const stageName = this.getPipelineStageName(pipeline, stageProbability);
          return this.translateService.instant(
            'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTIVITY.CHANGED_PIPELINE_STAGE',
            {
              opportunityName,
              pipelineName,
              stageName,
            },
          );
        }),
        catchError((err) => of(this.getActivityErrorMessage(err))),
      );
    }

    if (opportunityStatus === OpportunityStatus.ClosedLost) {
      return combineLatest([pipeline$, opportunityName$]).pipe(
        map(([pipeline, opportunityName]) => {
          const pipelineName = this.getPipelineName(pipeline, pipelineId);
          return this.translateService.instant(
            'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTIVITY.CHANGED_TO_CLOSED_STATUS_WITH_LOST_REASON',
            {
              pipelineName: pipelineName,
              closedLostReason: this.translateService.instant(CloseLostReasonFuncs.I18NKey(closedLostReason)),
              opportunityName,
            },
          );
        }),
        catchError((err) => of(this.getActivityErrorMessage(err))),
      );
    }

    return combineLatest([pipeline$, opportunityName$]).pipe(
      map(([pipeline, opportunityName]) => {
        const pipelineName = this.getPipelineName(pipeline, pipelineId);
        return this.translateService.instant(
          'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.ACTIVITY.CHANGED_TO_CLOSED_STATUS',
          {
            pipelineName: pipelineName,
            status: this.translateService.instant(OpportunityStatusFuncs.I18NKey(opportunityStatus)),
            opportunityName,
          },
        );
      }),
      catchError((err) => of(this.getActivityErrorMessage(err))),
    );
  }
}
